import { useEffect, useState } from 'react';
import { ApiError, api, type ParamDef } from '../api';
import { Banner, Field, Pill, Spinner, useAsync } from '../components/Common';
import { ParamForm, defaultParamValues } from '../components/ParamForm';
import { formatDateTime, relativeTime } from '../format';

interface Schedule {
  id: number;
  name: string;
  report_id: number;
  report_name: string;
  report_slug: string;
  cron: string;
  timezone: string;
  params: Record<string, any>;
  delivery: { formats?: string[]; email?: { to?: string[]; cc?: string[]; subject?: string; bodyNote?: string } };
  enabled: boolean;
  last_run_at: string | null;
  last_status: string | null;
  next_run_at: string | null;
  created_by_name: string | null;
}

const PRESETS = [
  { label: 'Every weekday at 07:30', cron: '30 7 * * 1-5' },
  { label: 'Every Monday at 08:00', cron: '0 8 * * 1' },
  { label: 'First of the month at 07:00', cron: '0 7 1 * *' },
  { label: 'Every hour', cron: '0 * * * *' },
];

export function Schedules() {
  const schedules = useAsync(() => api.get<Schedule[]>('/api/schedules'));
  const reports = useAsync(() => api.get<{ id: number; slug: string; name: string; params: ParamDef[] }[]>('/api/reports'));
  const [editing, setEditing] = useState<Schedule | 'new' | null>(null);
  const [message, setMessage] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const runNow = async (schedule: Schedule) => {
    setBusyId(schedule.id);
    setMessage(null);
    try {
      const res = await api.post<{ last_status: string }>(`/api/schedules/${schedule.id}/run-now`);
      setMessage({
        tone: res.last_status?.startsWith('ok') ? 'good' : 'error',
        text: `“${schedule.name}”: ${res.last_status ?? 'completed'}`,
      });
      schedules.reload();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusyId(null);
    }
  };

  if (schedules.loading && !schedules.data) return <Spinner label="Loading schedules…" />;
  if (schedules.error) return <Banner tone="error">{schedules.error}</Banner>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Schedules</h1>
          <p>
            Scheduled runs always re-query the source systems rather than serving cached data, and are delivered even when
            partial — clearly labelled — because silence looks like lateness rather than a broken integration.
          </p>
        </div>
        <div className="page-actions">
          <button className="primary" onClick={() => setEditing('new')}>
            New schedule
          </button>
        </div>
      </div>

      {message && <Banner tone={message.tone}>{message.text}</Banner>}

      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Schedule</th>
                <th>Report</th>
                <th>When</th>
                <th>Recipients</th>
                <th>Last run</th>
                <th>Next</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(schedules.data ?? []).map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.name}</strong> {s.enabled ? null : <Pill tone="off">paused</Pill>}
                  </td>
                  <td>{s.report_name}</td>
                  <td className="mono">{s.cron}</td>
                  <td>{(s.delivery.email?.to ?? []).length || '—'}</td>
                  <td>
                    {s.last_run_at ? (
                      <>
                        {relativeTime(s.last_run_at)}{' '}
                        {s.last_status?.startsWith('ok') ? <Pill tone="ok">ok</Pill> : <Pill tone="bad">{s.last_status}</Pill>}
                      </>
                    ) : (
                      'never'
                    )}
                  </td>
                  <td>{s.enabled ? formatDateTime(s.next_run_at) : '—'}</td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="small" onClick={() => runNow(s)} disabled={busyId === s.id}>
                      {busyId === s.id ? 'Running…' : 'Run now'}
                    </button>{' '}
                    <button className="small" onClick={() => setEditing(s)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!(schedules.data ?? []).length && <p className="note">No schedules yet.</p>}
      </div>

      {editing && (
        <ScheduleEditor
          schedule={editing === 'new' ? null : editing}
          reports={reports.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            schedules.reload();
          }}
        />
      )}
    </>
  );
}

function ScheduleEditor({
  schedule,
  reports,
  onClose,
  onSaved,
}: {
  schedule: Schedule | null;
  reports: { id: number; slug: string; name: string; params: ParamDef[] }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: schedule?.name ?? '',
    report_id: schedule?.report_id ?? reports[0]?.id ?? 0,
    cron: schedule?.cron ?? '30 7 * * 1-5',
    timezone: schedule?.timezone ?? 'Europe/London',
    enabled: schedule?.enabled ?? true,
  });
  const report = reports.find((r) => r.id === form.report_id);
  const [params, setParams] = useState<Record<string, any>>(schedule?.params ?? {});
  const [formats, setFormats] = useState<string[]>(schedule?.delivery.formats ?? ['pdf']);
  const [to, setTo] = useState((schedule?.delivery.email?.to ?? []).join(', '));
  const [subject, setSubject] = useState(schedule?.delivery.email?.subject ?? '');
  const [bodyNote, setBodyNote] = useState(schedule?.delivery.email?.bodyNote ?? '');
  const [preview, setPreview] = useState<string[] | null>(null);
  const [cronError, setCronError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (report && Object.keys(params).length === 0) setParams(defaultParamValues(report.params));
  }, [report?.id]);

  // Show the next three fire times as the cron is typed. A cron expression is
  // easy to get subtly wrong, and "next: Monday 07:30" is the only readable check.
  useEffect(() => {
    let cancelled = false;
    api
      .post<{ ok: boolean; error?: string; next?: string[] }>('/api/schedules/validate-cron', {
        cron: form.cron,
        timezone: form.timezone,
      })
      .then((res) => {
        if (cancelled) return;
        setCronError(res.ok ? null : res.error ?? 'Invalid schedule');
        setPreview(res.next ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [form.cron, form.timezone]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        ...form,
        params,
        delivery: {
          formats,
          email: {
            to: to.split(',').map((s) => s.trim()).filter(Boolean),
            subject: subject || undefined,
            bodyNote: bodyNote || undefined,
          },
        },
      };
      if (schedule) await api.put(`/api/schedules/${schedule.id}`, payload);
      else await api.post('/api/schedules', payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!schedule) return;
    setBusy(true);
    try {
      await api.del(`/api/schedules/${schedule.id}`);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 16, borderColor: 'var(--brand)' }}>
      <h3>{schedule ? `Edit “${schedule.name}”` : 'New schedule'}</h3>
      {error && <Banner tone="error">{error}</Banner>}

      <div className="row">
        <Field label="Name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Report">
          <select value={form.report_id} onChange={(e) => setForm({ ...form, report_id: Number(e.target.value) })}>
            {reports.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="row">
        <Field label="Schedule (cron)" help="Minute hour day-of-month month day-of-week.">
          <input value={form.cron} className="mono" onChange={(e) => setForm({ ...form, cron: e.target.value })} />
        </Field>
        <Field label="Timezone" help="Kept explicit so the report does not shift by an hour across BST changes.">
          <input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {PRESETS.map((p) => (
          <button key={p.cron} className="small ghost" onClick={() => setForm({ ...form, cron: p.cron })}>
            {p.label}
          </button>
        ))}
      </div>

      {cronError ? (
        <Banner tone="error">{cronError}</Banner>
      ) : preview ? (
        <Banner tone="info" title="Next three runs: ">
          {preview.map((iso) => formatDateTime(iso)).join('  ·  ')}
        </Banner>
      ) : null}

      {report && report.params.length > 0 && (
        <>
          <h3 style={{ marginTop: 16 }}>Parameters for the scheduled run</h3>
          <div className="row">
            <ParamForm defs={report.params} values={params} onChange={setParams} />
          </div>
        </>
      )}

      <h3 style={{ marginTop: 12 }}>Delivery</h3>
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 13 }}>
        {['pdf', 'xlsx'].map((format) => (
          <label key={format} style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={formats.includes(format)}
              onChange={(e) => setFormats(e.target.checked ? [...formats, format] : formats.filter((f) => f !== format))}
            />
            Attach {format.toUpperCase()}
          </label>
        ))}
      </div>

      <Field label="Email to" help="Comma separated. Leave empty to store the run in the platform without emailing anyone.">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="manager@spencerclarkegroup.co.uk, …" />
      </Field>
      <div className="row">
        <Field label="Subject" help="Defaults to the report name. A status prefix is added automatically if the run was partial.">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Field>
        <Field label="Note in the email body">
          <input value={bodyNote} onChange={(e) => setBodyNote(e.target.value)} />
        </Field>
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 14 }}>
        <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
        Active
      </label>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="primary" onClick={save} disabled={busy || Boolean(cronError)}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onClose} disabled={busy}>
          Cancel
        </button>
        {schedule && (
          <button className="danger" onClick={remove} disabled={busy} style={{ marginLeft: 'auto' }}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
